import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getContacts, mergeContacts, exportContacts, importContacts, downloadContactsTemplate } from '../api/contacts';
import { ContactInterface } from '../types';
import ContactsTable from './ContactsTable';
import ImportErrorModal from './ImportErrorModal';
import ContactDetailsModal from './ContactDetailsModal';
import ContactsSearchBar from './ContactsSearchBar';
import AddContactModal from './AddContactModal';
import MergeContactsModal from './MergeContactsModal';

function parseQuery(search: string) {
  const params = new URLSearchParams(search);
  return {
    name: params.get('name') || '',
    variant_id: params.get('variant_id') || '',
    email: params.get('email') || '',
    phone: params.get('phone') || '',
  };
}

interface SearchParams {
  name?: string;
  variant_id?: string;
  email?: string;
  phone?: string;
}

function Contacts(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState<ContactInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const selectedContactId = searchParams.get('contactId');
  const modalOpen = searchParams.get('modal') === 'details';
  const addContactOpen = searchParams.get('modal') === 'add';

  const [currentSearchParams, setCurrentSearchParams] = useState<SearchParams>(() => parseQuery(location.search));
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContacts, setSelectedContacts] = useState<ContactInterface[]>([]);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importErrorsModalOpen, setImportErrorsModalOpen] = useState(false);



  useEffect(() => {
    if (currentSearchParams.name || currentSearchParams.variant_id) {
      handleSearch(currentSearchParams, page);
    } else {
      fetchContacts(page);
    }
    // eslint-disable-next-line
  }, [currentSearchParams.name, currentSearchParams.variant_id, page]);

  const fetchContacts = async (pageNum: number): Promise<void> => {
    setLoading(true);
    try {
      const response = await getContacts(`?page=${pageNum}&page_size=${pageSize}`);
      setContacts(response.data || []);
      setTotal(response.total || 0);
      setTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (params: SearchParams, pageNum: number): Promise<void> => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set('method', 'search');
      if (params.name) query.set('name', params.name);
      if (params.variant_id) query.set('variant_id', params.variant_id);
      if (params.email) query.set('email', params.email);
      if (params.phone) query.set('phone', params.phone);
      query.set('page', String(pageNum));
      query.set('page_size', String(pageSize));
      navigate({ pathname: '/contacts', search: `?${query.toString()}` }, { replace: true });
      const response = await getContacts(`?${query.toString()}`);
      setContacts(response.data || []);
      setTotal(response.total || 0);
      setTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error('Error searching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleContactClick = (contact: ContactInterface): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('modal', 'details');
    newParams.set('contactId', contact.id);
    setSearchParams(newParams);
  };

  const handleCloseModal = (): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('modal');
    newParams.delete('contactId');
    setSearchParams(newParams);
  };

  const handleOpenAddContact = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('modal', 'add');
    setSearchParams(newParams);
  };

  const handleCloseAddContact = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('modal');
    setSearchParams(newParams);
  };

  const handleSearchBar = (params: SearchParams) => {
    setCurrentSearchParams(params);
    setPage(1);
    handleSearch(params, 1);
  };

  const handleExport = async () => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('page_size', total ? total.toString() : '100');
    if (currentSearchParams.name) params.set('method', 'search');
    if (currentSearchParams.name) params.set('name', currentSearchParams.name);
    if (currentSearchParams.variant_id) params.set('variant_id', currentSearchParams.variant_id);
    if (currentSearchParams.email) params.set('email', currentSearchParams.email);
    if (currentSearchParams.phone) params.set('phone', currentSearchParams.phone);
    await exportContacts(`?${params.toString()}`);
  };

  const handleImport = async (data: any[]) => {
    try {
      await importContacts(data);
      alert('הייבוא הושלם בהצלחה!');
      fetchContacts(page);
    } catch (err: any) {
      if (err.errors) {
        setImportErrors(err.errors);
        setImportErrorsModalOpen(true);
      } else {
        alert(err.message || 'שגיאה בייבוא הנתונים');
      }
    }
  };

  const handleContactCreated = () => {
    setPage(1);
    fetchContacts(1);
  };

  const handleContactUpdated = () => {
    if (Object.keys(currentSearchParams).length > 0) {
      handleSearch(currentSearchParams, page);
    } else {
      fetchContacts(page);
    }
  };

  const handleSelectContact = (contactId: string, checked: boolean) => {
    setSelectedContacts(prev => {
      if (checked) {
        if (prev.length < 2) {
          const contact = contacts.find(c => c.id === contactId);
          if (contact && !prev.some(c => c.id === contactId)) {
            return [...prev, contact];
          }
        }
        return prev;
      } else {
        return prev.filter(c => c.id !== contactId);
      }
    });
  };

  const handleOpenMergeModal = () => setMergeModalOpen(true);
  const handleCloseMergeModal = () => setMergeModalOpen(false);

  const handleMerge = async (masterId: string, otherId: string) => {
    await mergeContacts(masterId, otherId);
    setMergeModalOpen(false);
    setSelectedContacts([]);
    fetchContacts(page);
  };

  return (
    <Box sx={{ maxWidth: '90%', margin: '20px auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">אנשי קשר</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            disabled={selectedContacts.length !== 2}
            onClick={handleOpenMergeModal}
          >
            מיזוג
          </Button>
          {selectedContacts.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setSelectedContacts([])}
            >
              נקה מיזוג
            </Button>
          )}
          <IconButton color="primary" onClick={handleOpenAddContact} sx={{ backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } }}>
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
      <ContactsSearchBar
        initialVariantId={currentSearchParams.variant_id}
        onSearch={handleSearchBar}
        loading={loading}
      />
      <ContactsTable
        contacts={contacts}
        loading={loading}
        onContactClick={handleContactClick}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={handlePageChange}
        selectedContactIds={selectedContacts.map(c => c.id)}
        onSelectContact={handleSelectContact}
        onExport={handleExport}
        onImport={handleImport}
        onDownloadTemplate={downloadContactsTemplate}
      />
      <MergeContactsModal
        open={mergeModalOpen}
        onClose={handleCloseMergeModal}
        contacts={selectedContacts}
        onMerge={handleMerge}
      />
      <ContactDetailsModal
        open={modalOpen}
        contactId={selectedContactId}
        onClose={handleCloseModal}
        onContactChanged={handleContactUpdated}
      />
      <AddContactModal open={addContactOpen} onClose={handleCloseAddContact} onContactCreated={handleContactCreated} />
      <ImportErrorModal
        open={importErrorsModalOpen}
        onClose={() => setImportErrorsModalOpen(false)}
        errors={importErrors}
      />
    </Box>
  );
}

export default Contacts; 